import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, RefreshCw } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { aiAssistantService } from '../../services/aiAssistantService';
import { Button } from '../ui/button';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

/**
 * AIAssistantChat Component
 * Chat interface for the AI assistant (authenticated users with data access)
 */
const AIAssistantChat = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const {
    assistantMessages,
    setAssistantMessages,
    isLoading,
    setIsLoading,
    getCurrentSessionId,
    setCurrentSessionId,
    roleConfig,
    userRole,
    closeChatbot,
  } = useChatbot();

  const [localMessages, setLocalMessages] = useState([]);

  // Initialize with welcome message if no messages
  useEffect(() => {
    if (assistantMessages.length === 0) {
      setAssistantMessages([{
        role: 'assistant',
        content: roleConfig.welcomeMessage,
        timestamp: new Date(),
      }]);
    }
  }, []);

  // Sync local messages with context
  useEffect(() => {
    setLocalMessages(assistantMessages);
  }, [assistantMessages]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [localMessages, scrollToBottom]);

  // Handle sending message
  const handleSendMessage = async (message) => {
    if (!message.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setAssistantMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await aiAssistantService.sendMessage(
        message,
        getCurrentSessionId()
      );

      setCurrentSessionId(response.sessionId);

      // Add assistant response with data and actions
      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        data: response.data,
        actions: response.actions,
        timestamp: new Date(),
      };

      setAssistantMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.',
        timestamp: new Date(),
      };
      
      setAssistantMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle action click from assistant messages
  const handleActionClick = (action) => {
    if (action.action === 'navigate' && action.params?.to) {
      closeChatbot();
      navigate(action.params.to);
    }
  };

  // Clear conversation
  const handleClearConversation = async () => {
    const sessionId = getCurrentSessionId();
    if (sessionId) {
      await aiAssistantService.clearSession(sessionId);
    }
    setCurrentSessionId(null);
    setAssistantMessages([{
      role: 'assistant',
      content: roleConfig.welcomeMessage,
      timestamp: new Date(),
    }]);
  };

  // Get header gradient based on role
  const getHeaderGradient = () => {
    switch (userRole) {
      case 'ADMIN':
        return 'from-purple-600 to-indigo-700';
      case 'PROFESSIONAL':
        return 'from-emerald-600 to-teal-700';
      default:
        return 'from-[#FF9F68] to-[#E67E4F]';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-gray-50 to-white min-h-0">
        {localMessages.map((msg, index) => (
          <ChatMessage
            key={index}
            message={msg}
            isUser={msg.role === 'user'}
            variant="assistant"
            onActionClick={handleActionClick}
          />
        ))}

        {/* Loading indicator */}
        {isLoading && <ChatMessage isLoading />}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick suggestions - show only when few messages */}
      {localMessages.length <= 1 && !isLoading && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">Suggestions :</p>
          <div className="flex flex-wrap gap-2">
            {roleConfig.suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSendMessage(suggestion)}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition-all shadow-sm"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-100 bg-white px-4 py-4">
        <ChatInput
          onSend={handleSendMessage}
          isLoading={isLoading}
          placeholder="Posez votre question..."
          suggestions={[]}
          variant="assistant"
        />
      </div>
    </div>
  );
};

export default AIAssistantChat;

